import { useCallback, useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import {
  type AnalyticsChatResult,
  useAnalyticsChatSession,
} from "src/analytics/copilot/useAnalyticsChatSession";
import { fetchWorkspaceFlowsSnapshot } from "src/analytics/workspace/workspaceFlowsSnapshot";
import { buildWorkspaceInsightsAgentContext } from "src/analytics/workspace/workspaceInsightsAgentContext";
import { useCurrentWorkspace, useFlows } from "src/api/queries";
import { useEnvironment } from "src/hooks/useEnvironment";

// Surface expiry on the FE a day before the backend hard limit so the user gets
// a graceful "start a new chat" message instead of a failed invoke at the edge.
const SESSION_EXPIRY_THRESHOLD_MS = 6 * 24 * 60 * 60 * 1000;
const SESSION_EXPIRED_MESSAGE =
  "This chat has expired. Start a new conversation to continue.";

const SESSIONS_STORAGE_KEY = "workspaceInsightsSessions";

const MAX_FLOW_NAME_CHARS = 40;

// Wire ids from the agents module's `COPILOT_VIRTUAL_AGENT_IDS`; no agents-table
// rows exist behind them.
const WORKSPACE_INSIGHTS_VIRTUAL_AGENT_ID =
  "ffffffff-0000-4000-8000-000000000007";
const WORKSPACE_INSIGHTS_VIRTUAL_VERSION_ID =
  "ffffffff-0000-4000-8000-000000000008";

export const useWorkspaceInsightsChat = ({
  wsId,
}: {
  wsId: string;
}): AnalyticsChatResult => {
  const [environment] = useEnvironment();

  const flowsQuery = useFlows({ workspaceId: wsId });
  const flowIdToName = useMemo(
    () =>
      Object.fromEntries(
        flowsQuery.data.map(
          (flow) => [flow.id, flow.name.slice(0, MAX_FLOW_NAME_CHARS)] as const,
        ),
      ),
    [flowsQuery.data],
  );
  // `isFullyLoaded` drops on every background refetch, which would disable the
  // snapshot mid-session and cancel an in-flight preflight. Latch it instead.
  const [flowNamesLoaded, setFlowNamesLoaded] = useState(false);
  useEffect(() => {
    if (flowsQuery.isFullyLoaded) setFlowNamesLoaded(true);
  }, [flowsQuery.isFullyLoaded]);
  const flowNamesUnavailable = !flowNamesLoaded && flowsQuery.isError;

  const { data: workspace } = useCurrentWorkspace();
  const baseUrl = workspace?.base_url;

  // Nothing to provision: the virtual agent needs no agent or version record,
  // so this resolves without a round trip and the first message goes straight
  // to invoke.
  const provision = useCallback(
    () => Promise.resolve(WORKSPACE_INSIGHTS_VIRTUAL_AGENT_ID),
    [],
  );

  const snapshotQuery = useQuery({
    queryKey: ["workspaceInsightsAgentContext", baseUrl, wsId],
    queryFn: () => fetchWorkspaceFlowsSnapshot(baseUrl as string),
    enabled: Boolean(baseUrl) && flowNamesLoaded,
    staleTime: Infinity,
    retry: 1,
    throwOnError: false,
  });

  const initialStructuredInput =
    flowNamesUnavailable || snapshotQuery.isError
      ? { environment }
      : snapshotQuery.data
        ? buildWorkspaceInsightsAgentContext({
            environment,
            flowVolumes: snapshotQuery.data.flowVolumes,
            flowIdToName,
            flowCount: snapshotQuery.data.flowCount,
          })
        : null;

  return useAnalyticsChatSession({
    storageKey: SESSIONS_STORAGE_KEY,
    sessionSlotKey: wsId,
    environment,
    provision,
    agentVersionId: WORKSPACE_INSIGHTS_VIRTUAL_VERSION_ID,
    provisionReady: flowNamesLoaded || flowNamesUnavailable,
    initialStructuredInput,
    sessionExpiryThresholdMs: SESSION_EXPIRY_THRESHOLD_MS,
    sessionExpiredMessage: SESSION_EXPIRED_MESSAGE,
    injectCurrentDate: true,
  });
};
